import { useState, useEffect } from 'react';
import { Car, ChevronRight, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { useCustomerVehicles } from '@/hooks/useCustomerVehicles';
import type { CustomerVehicle } from '@/lib/customers/types';
import { formatEngineSizeDisplay } from '../../../supabase/functions/_shared/vehicleProfile/engineDisplay';

interface CustomerVehicleSelectorProps {
  customerId: string;
  onSelectVehicle: (vehicle: CustomerVehicle) => void;
  onCreateNewVehicle: () => void;
}

export function CustomerVehicleSelector({
  customerId,
  onSelectVehicle,
  onCreateNewVehicle,
}: CustomerVehicleSelectorProps) {
  const queryResult = useCustomerVehicles(customerId);
  const vehicles = queryResult.data || [];
  const isLoading = queryResult.isLoading;
  const error = queryResult.error;

  if (isLoading) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium">Select Vehicle</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {[1, 2].map((i) => (
            <Skeleton key={i} className="h-16 w-full" />
          ))}
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="p-4 text-sm text-destructive">
          Error loading vehicles: {error.message}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <Car className="h-4 w-4" />
          Select Vehicle ({vehicles.length} on file)
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {vehicles.length === 0 ? (
          <div className="text-sm text-muted-foreground py-2">
            No vehicles on file for this customer.
          </div>
        ) : (
          vehicles.map((vehicle) => (
            <button
              key={vehicle.id}
              type="button"
              className="w-full p-3 text-left bg-muted/50 hover:bg-muted rounded-md border transition-colors flex items-center gap-3"
              onClick={() => onSelectVehicle(vehicle)}
            >
              <Car className="h-5 w-5 text-muted-foreground shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="font-medium">
                  {vehicle.year} {vehicle.make} {vehicle.model}
                </div>
                <div className="text-xs text-muted-foreground flex flex-wrap gap-2 mt-1">
                  {vehicle.vin && (
                    <span>VIN: ...{vehicle.vin.slice(-6)}</span>
                  )}
                  {vehicle.license_plate && (
                    <span>Plate: {vehicle.license_plate}</span>
                  )}
                  {vehicle.engine_size && (
                    <span>Engine: {formatEngineSizeDisplay(vehicle.engine_size)}</span>
                  )}
                </div>
              </div>
              <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
            </button>
          ))
        )}
        
        <Button
          type="button"
          variant="outline"
          className="w-full mt-2"
          onClick={onCreateNewVehicle}
        >
          <Plus className="h-4 w-4 mr-2" />
          Add New Vehicle
        </Button>
      </CardContent>
    </Card>
  );
}
